import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Building2, CheckCircle2, Clock3, FileCheck2, GitBranch, Globe2, KeyRound, Link2, MoveRight, Pencil, PlusCircle, RotateCcw, ShieldAlert, ShieldCheck, Trash2, UserMinus, UserPlus, UsersRound, XCircle } from "lucide-react";
import type {
  ApprovalBusinessInputField,
  ApprovalOperationDirectoryItem,
  ApprovalOperationDirectoryResponse,
  ApprovalOperationType,
  ApprovalPreviewResponse,
  ApprovalTargetOption,
  ApprovalTargetOptionsResponse,
  ApprovalTargetSelection,
  EnterpriseApprovalCreateInput,
  EnterpriseApprovalPreviewInput,
  EnterpriseIdentityConfigView,
  EnterpriseIdentityConsole,
  EnterpriseIdentityDomainView,
  EnterpriseIdentityTenantOption,
  EnterpriseIdentityUpsertInput,
  EnterpriseOrgUnitMember,
  EnterpriseOrgUnitMemberCandidate,
  EnterpriseOrgUnitTenantScope,
  EnterpriseOrgUnitTreeNode,
  EnterpriseOrgUnitTreeResponse,
  PlatformRole,
  SessionView,
} from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { RemotePageState } from "../components/RemotePageState";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog } from "../components/Overlay";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { InlineFeedback } from "../components/Feedback";
import { V3Select } from "../components/V3Select";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";
import { Wizard, type WizardStep } from "../components/Wizard";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";

type SsoMode = EnterpriseIdentityConfigView["ssoMode"];
type TenantStatus = EnterpriseIdentityTenantOption["status"];
type TenantListItem = { id: string; key?: string; name: string; status?: string | null; selectable?: boolean; disabledReason?: string | null };
type LegacyIdentityConfig = {
  tenantId: string;
  tenantName?: string | null;
  verifiedDomains?: string[];
  ssoMode?: SsoMode | string | null;
  ssoStatus?: EnterpriseIdentityConfigView["ssoStatus"] | string | null;
  scimStatus?: EnterpriseIdentityConfigView["scimStatus"] | string | null;
  updatedAt?: string | null;
  lastError?: string | null;
};
type IdentityConsoleResponse = Partial<EnterpriseIdentityConsole> & { items?: Array<EnterpriseIdentityConfigView | LegacyIdentityConfig> };
type OrganizationConsoleResponse = Partial<EnterpriseOrgUnitTreeResponse>;
type TenantResponse = { items: TenantListItem[] };
type LoginPolicyDraft = EnterpriseIdentityUpsertInput["loginPolicy"];
type IdentityDraft = { tenantId: string; domains: string[]; ssoMode: SsoMode; loginPolicy: LoginPolicyDraft; reason: string };
type EnterpriseTab = "identity" | "organization" | "approval" | "audit";
type OrgUnitFlat = { unit: EnterpriseOrgUnitTreeNode; pathText: string; optionLabel: string };
type OrgUnitDialogState =
  | { mode: "create"; parent: EnterpriseOrgUnitTreeNode | null }
  | { mode: "rename"; unit: EnterpriseOrgUnitTreeNode }
  | { mode: "move"; unit: EnterpriseOrgUnitTreeNode };
type OrgTenantDialogState = { unit: EnterpriseOrgUnitTreeNode } | null;
type OrgMemberDialogState = { unit: EnterpriseOrgUnitTreeNode } | null;
type OrgRemoveMemberState = { unit: EnterpriseOrgUnitTreeNode; member: EnterpriseOrgUnitMember } | null;
type OrgDeleteState = { unit: EnterpriseOrgUnitTreeNode } | null;
type ApprovalInputFieldView = Omit<ApprovalBusinessInputField, "type" | "options"> & {
  type: ApprovalBusinessInputField["type"] | "fixed" | "checkbox";
  value?: unknown;
  options?: Array<{ value: string | number | boolean; label: string; description?: string; summary?: string }>;
};
type ApprovalDirectoryItemView = Omit<ApprovalOperationDirectoryItem, "inputFields"> & { inputFields?: ApprovalInputFieldView[] };
type ApprovalConsole = { operations: ApprovalDirectoryItemView[]; approvals: ApprovalListItemView[] };
type ApprovalListItemView = {
  id: string;
  operationType: ApprovalOperationType;
  operationLabel: string;
  targetLabel: string;
  targetSummary: string;
  status: "pending" | "approved" | "rejected" | string;
  reason: string;
  requestedByName: string;
  isSelfRequested: boolean;
  canDecide: boolean;
  createdAt: string;
  expiresAt: string;
  impact: string[];
  recovery: string;
};
type ApprovalDraft = {
  operationType: ApprovalOperationType | "";
  targetValue: string;
  targetOptions: ApprovalTargetOption[];
  targetLoading: boolean;
  targetError: string;
  payload: Record<string, unknown>;
  reason: string;
  preview: ApprovalPreviewResponse | null;
  previewLoading: boolean;
  previewError: string;
  submitting: boolean;
  submitError: string;
};
type ApprovalDecisionState = { item: ApprovalListItemView; decision: "approved" | "rejected" } | null;

const allTenantsValue = "all";
const rootParentValue = "__root__";
const defaultReason = "更新企业身份接入配置";
const defaultOrgReason = "调整企业组织架构";
const tenantStatuses = new Set(["provisioning", "active", "suspended", "archived", "purged"]);
const tenantStatusLabels: Record<TenantStatus, string> = {
  provisioning: "开通中",
  active: "已启用",
  suspended: "已暂停",
  archived: "已归档",
  purged: "已清除",
};
const adminStatusLabels: Record<EnterpriseOrgUnitMember["status"], string> = {
  active: "可用",
  suspended: "已停用",
  deleted: "已删除",
};
const roleLabels: Record<PlatformRole, string> = {
  platform_owner: "平台负责人",
  security_admin: "安全管理员",
  tenant_operator: "租户运营员",
  support_operator: "代管操作员",
  billing_operator: "账务运营员",
  auditor: "只读审计员",
};
const ssoModeLabels: Record<SsoMode, string> = {
  disabled: "仅账号密码登录",
  optional: "账号密码与单点登录并行",
  required: "必须使用单点登录",
};
const ssoModeHints: Record<SsoMode, string> = {
  disabled: "关闭单点登录后，成员仍可用平台账号密码登录。",
  optional: "保存后仍为待验证，需要到身份服务商完成配置。",
  required: "保存后仍为待验证，确认可用前请谨慎对全员强制。",
};
const passwordLoginOptions = [
  { value: "enabled", label: "允许账号密码登录", hint: "适合单点登录尚未验证或需要保留兜底入口。" },
  { value: "disabled", label: "关闭账号密码登录", hint: "仅建议在单点登录通过平台核验后使用。" },
];
const mfaOptions = [
  { value: "required", label: "要求动态验证码", hint: "提升管理员与成员登录安全性。" },
  { value: "optional", label: "不强制动态验证码", hint: "适合接入初期或仅做域名验证。" },
];
const domainLimitOptions = [
  { value: "enabled", label: "仅允许已登记域名成员", hint: "需要至少登记一个企业域名。" },
  { value: "disabled", label: "不限制企业域名", hint: "适合过渡期或域名验证未完成时。" },
];
const defaultApprovalReason = "请说明业务背景、影响范围和恢复责任人。";
const approvalStatusLabels: Record<string, string> = {
  pending: "等待审批",
  approved: "已通过",
  rejected: "已拒绝",
  expired: "已过期",
  executed: "已执行",
};
const approvalCategoryLabels: Record<string, string> = {
  tenant: "租户生命周期",
  governance: "企业治理",
  admin: "管理员",
  commercial: "商业订阅",
  billing: "账单退款",
  security: "安全凭据",
  operations: "运营变更",
};
const approvalTargetFieldByOperation: Record<ApprovalOperationType, keyof ApprovalTargetSelection> = {
  "tenant.activate": "tenantId",
  "tenant.suspend": "tenantId",
  "tenant.close": "tenantId",
  "tenant.purge": "tenantId",
  "admin.create": "adminEmail",
  "admin.update": "adminId",
  "admin.delete": "adminId",
  "plan.publish": "planVersionId",
  "addon.publish": "addonVersionId",
  "plan.force_migration": "subscriptionChangeId",
  "subscription.change": "subscriptionChangeId",
  "discount_rule.approve": "discountRuleId",
  "payment.offline_confirm": "purchaseOrderId",
  "invoice.adjust": "invoiceId",
  "refund.approve": "refundId",
  "refund.execute": "refundId",
  "usage.adjust": "usageMeterId",
  "quota_override.create": "usageMeterId",
  "credential.rotate": "credentialId",
  "credential.revoke": "credentialId",
  "credential.rollback": "credentialId",
  "support.approve": "supportGrantId",
  "feature_flag.adjust": "featureFlagKey",
  "feature_flag.full_publish": "featureFlagKey",
  "incident.open": "incidentId",
  "incident.transition": "incidentId",
  "incident.resolve_with_recovery": "incidentId",
  "maintenance.schedule": "maintenanceWindowId",
  "maintenance.cancel": "maintenanceWindowId",
  "maintenance.end_early": "maintenanceWindowId",
  "feature_flag.schedule_change": "featureFlagKey",
  "feature_flag.rollback": "featureFlagKey",
};

function hasAction(session: SessionView, action: string): boolean {
  return (session.actions as readonly string[]).includes(action);
}

function asSsoMode(value: string | null | undefined): SsoMode {
  return value === "required" || value === "optional" || value === "disabled" ? value : "disabled";
}

function asTenantStatus(value: string | null | undefined): TenantStatus {
  return value && tenantStatuses.has(value) ? value as TenantStatus : "active";
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function safeEvidenceText(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  if (/^[a-z0-9_.:-]+$/i.test(value.trim())) return fallback;
  return value.trim().slice(0, 140);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "暂无证据时间";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "证据时间待确认";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function normalizeDomain(value: string): string {
  return value.trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "").toLowerCase();
}

function isDomain(value: string): boolean {
  return /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z0-9-]{2,63}$/i.test(value);
}

function createIdempotencyKey(): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `enterprise-identity-${suffix}`.slice(0, 160);
}

function createOrgIdempotencyKey(operation: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `enterprise-org-${operation}-${suffix}`.slice(0, 160);
}

function normalizeOrganizationConsole(data: OrganizationConsoleResponse): EnterpriseOrgUnitTreeResponse {
  return {
    items: Array.isArray(data.items) ? data.items : [],
    memberCandidates: Array.isArray(data.memberCandidates) ? data.memberCandidates : [],
  };
}

function businessCopy(value: string | null | undefined, fallback: string): string {
  const base = safeEvidenceText(value, fallback);
  return base
    .replaceAll("\u670d\u52a1\u7aef", "平台")
    .replaceAll("\u56de\u8bfb", "刷新查看")
    .replaceAll("\u5185\u90e8\u7f16\u53f7", "编号")
    .replaceAll("\u5185\u90e8\u6807\u8bc6", "编号")
    .replaceAll("\u6280\u672f\u7f16\u53f7", "编号")
    .slice(0, 500);
}

function normalizeApprovalConsole(directory: Partial<ApprovalOperationDirectoryResponse>, list: { items?: ApprovalListItemView[] }): ApprovalConsole {
  return {
    operations: arrayOf(directory.items).map((item) => normalizeApprovalOperation(item)).filter((item): item is ApprovalDirectoryItemView => Boolean(item)),
    approvals: arrayOf(list.items).map((item) => normalizeApprovalListItem(item)).filter((item): item is ApprovalListItemView => Boolean(item)),
  };
}

function normalizeApprovalOperation(value: unknown): ApprovalDirectoryItemView | null {
  const record = recordOf(value);
  const operationType = stringOf(record.operationType) as ApprovalOperationType | null;
  if (!operationType || !(operationType in approvalTargetFieldByOperation)) return null;
  return {
    operationType,
    label: businessCopy(stringOf(record.label), "审批动作"),
    description: businessCopy(stringOf(record.description), "请确认影响范围后再发起审批。"),
    category: stringOf(record.category) ?? "governance",
    targetLabel: businessCopy(stringOf(record.targetLabel), "业务对象"),
    riskLevel: "high",
    previewRequired: true,
    inputFields: arrayOf(record.inputFields).map(normalizeApprovalInputField).filter((item): item is ApprovalInputFieldView => Boolean(item)),
    disabled: Boolean(record.disabled),
    unavailableReason: stringOf(record.unavailableReason),
  };
}

function normalizeApprovalInputField(value: unknown): ApprovalInputFieldView | null {
  const record = recordOf(value);
  const key = stringOf(record.key);
  const label = businessCopy(stringOf(record.label), "业务输入");
  const typeText = stringOf(record.type) ?? "text";
  if (!key || !/^[a-zA-Z][a-zA-Z0-9_.-]{0,79}$/.test(key)) return null;
  const type = (["text", "textarea", "select", "boolean", "percent", "money", "datetime", "secret_digest", "fixed", "checkbox"].includes(typeText) ? typeText : "text") as ApprovalInputFieldView["type"];
  const options: NonNullable<ApprovalInputFieldView["options"]> = [];
  for (const option of arrayOf(record.options)) {
    const optionRecord = recordOf(option);
    const optionValue = optionRecord.value;
    if (!["string", "number", "boolean"].includes(typeof optionValue)) continue;
    options.push({
      value: optionValue as string | number | boolean,
      label: businessCopy(stringOf(optionRecord.label), String(optionValue)),
      description: businessCopy(stringOf(optionRecord.description) ?? stringOf(optionRecord.summary), ""),
      summary: businessCopy(stringOf(optionRecord.summary) ?? stringOf(optionRecord.description), ""),
    });
  }
  return {
    key,
    label,
    type,
    required: record.required !== false,
    readonly: Boolean(record.readonly),
    value: record.value,
    helperText: businessCopy(stringOf(record.helperText), ""),
    options,
  };
}

function normalizeApprovalListItem(value: unknown): ApprovalListItemView | null {
  const record = recordOf(value);
  const id = stringOf(record.id);
  const operationType = stringOf(record.operationType) as ApprovalOperationType | null;
  if (!id || !operationType || !(operationType in approvalTargetFieldByOperation)) return null;
  return {
    id,
    operationType,
    operationLabel: businessCopy(stringOf(record.operationLabel), "审批动作"),
    targetLabel: businessCopy(stringOf(record.targetLabel), "业务对象"),
    targetSummary: businessCopy(stringOf(record.targetSummary), "影响范围待确认"),
    status: stringOf(record.status) ?? "pending",
    reason: businessCopy(stringOf(record.reason), "未填写原因"),
    requestedByName: businessCopy(stringOf(record.requestedByName), "申请人"),
    isSelfRequested: Boolean(record.isSelfRequested),
    canDecide: Boolean(record.canDecide),
    createdAt: stringOf(record.createdAt) ?? "",
    expiresAt: stringOf(record.expiresAt) ?? "",
    impact: arrayOf(record.impact).map((item) => businessCopy(stringOf(item), "影响范围待确认")),
    recovery: businessCopy(stringOf(record.recovery), "按恢复清单重新发起审批。"),
  };
}

function approvalCategoryText(category: string): string {
  return approvalCategoryLabels[category] ?? (/^[a-z0-9_.-]+$/i.test(category) ? "业务变更" : category);
}

function approvalStatusText(status: string): string {
  return approvalStatusLabels[status] ?? "状态待确认";
}

function approvalTargetFor(_operationType: ApprovalOperationType, value: string): EnterpriseApprovalPreviewInput["target"] {
  return { selectionToken: value };
}

function defaultApprovalPayload(operationType: ApprovalOperationType, fields: ApprovalInputFieldView[]): Record<string, unknown> {
  const payload: Record<string, unknown> = operationType === "admin.update" ? { op: "update" } : {};
  for (const field of fields) {
    if (field.type === "fixed") {
      payload[field.key] = field.value ?? "";
    } else if (field.type === "boolean" || field.type === "checkbox") {
      payload[field.key] = Boolean(field.value);
    } else if (field.type === "select") {
      const first = field.options?.[0];
      if (first) payload[field.key] = first.value;
    } else if (field.readonly && field.value !== undefined) {
      payload[field.key] = field.value;
    }
  }
  return payload;
}

function fieldValueAsString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function approvalTargetOptions(targets: ApprovalTargetOption[]) {
  return targets.map((target) => ({
    value: target.selectionToken,
    label: target.statusLabel ? `${businessCopy(target.label, "业务对象")} · ${businessCopy(target.statusLabel, "状态待确认")}` : businessCopy(target.label, "业务对象"),
    hint: target.disabled ? businessCopy(target.disabledReason, "暂不可选择") : businessCopy(target.summary, "影响范围待确认"),
    disabled: Boolean(target.disabled),
  }));
}

function flattenOrgUnits(items: EnterpriseOrgUnitTreeNode[]): OrgUnitFlat[] {
  const output: OrgUnitFlat[] = [];
  const walk = (unit: EnterpriseOrgUnitTreeNode) => {
    const pathText = unit.path.map((item) => item.name).join(" / ");
    output.push({ unit, pathText, optionLabel: `${"— ".repeat(Math.max(0, unit.depth))}${unit.name}` });
    unit.children.forEach(walk);
  };
  items.forEach(walk);
  return output;
}

function collectDescendantIds(unit: EnterpriseOrgUnitTreeNode): Set<string> {
  const ids = new Set<string>();
  const walk = (node: EnterpriseOrgUnitTreeNode) => {
    for (const child of node.children) {
      ids.add(child.id);
      walk(child);
    }
  };
  walk(unit);
  return ids;
}

function orgPathLabel(unit: EnterpriseOrgUnitTreeNode): string {
  return unit.path.map((item) => item.name).join(" / ");
}

function selectableTenantOptions(tenants: EnterpriseIdentityTenantOption[], currentUnit: EnterpriseOrgUnitTreeNode | null) {
  const currentTenantIds = new Set((currentUnit?.tenantScope ?? []).map((tenant) => tenant.tenantId));
  return tenants.map((tenant) => {
    const archived = tenant.status === "archived" || tenant.status === "purged";
    const alreadyHere = currentTenantIds.has(tenant.id);
    return {
      value: tenant.id,
      label: `${tenant.name} · ${tenantStatusLabels[tenant.status]}`,
      hint: alreadyHere ? "已在当前组织范围" : tenant.disabledReason ?? "分配后只改变组织归属，不改变租户启用状态。",
      disabled: archived || alreadyHere || !tenant.selectable,
    };
  });
}

function unitMoveOptions(flatUnits: OrgUnitFlat[], unit: EnterpriseOrgUnitTreeNode) {
  const blocked = collectDescendantIds(unit);
  blocked.add(unit.id);
  return [
    { value: rootParentValue, label: "作为顶级组织", hint: "移动到组织架构最上层。" },
    ...flatUnits.map(({ unit: item, pathText, optionLabel }) => ({
      value: item.id,
      label: optionLabel,
      hint: pathText,
      disabled: blocked.has(item.id),
    })),
  ];
}

function unitParentOptions(flatUnits: OrgUnitFlat[]) {
  return [
    { value: rootParentValue, label: "作为顶级组织", hint: "用于集团、事业群或其他一级范围。" },
    ...flatUnits.map(({ unit, pathText, optionLabel }) => ({ value: unit.id, label: optionLabel, hint: pathText })),
  ];
}

function canUseReason(reason: string): boolean {
  return reason.trim().length >= 8;
}

function firstEnabled(options: Array<{ value: string; disabled?: boolean }>): string {
  return options.find((option) => !option.disabled)?.value ?? "";
}

function domainViewFromUnknown(value: unknown): EnterpriseIdentityDomainView | null {
  const record = recordOf(value);
  const domain = stringOf(record.domain);
  if (!domain) return null;
  const status = record.status === "verified" || record.status === "pending_verification" || record.status === "verification_failed" ? record.status : "pending_verification";
  return {
    domain,
    status,
    evidenceAt: stringOf(record.evidenceAt),
    evidenceResult: safeEvidenceText(stringOf(record.evidenceResult), "尚未验证，需去身份服务商完成。"),
    recoveryAction: safeEvidenceText(stringOf(record.recoveryAction), "去身份服务商完成域名验证后再回到本页查看。"),
  };
}

function legacyIdentityToView(item: LegacyIdentityConfig): EnterpriseIdentityConfigView {
  const domains = (item.verifiedDomains ?? []).map((domain) => ({
    domain,
    status: "pending_verification",
    evidenceAt: null,
    evidenceResult: "尚未验证，需去身份服务商完成。",
    recoveryAction: "去身份服务商完成域名归属验证；平台核验前不会显示已验证。",
  } satisfies EnterpriseIdentityDomainView));
  const ssoMode = asSsoMode(item.ssoMode);
  return {
    tenantId: item.tenantId,
    tenantName: item.tenantName ?? "企业名称待确认",
    domains,
    verifiedDomains: item.verifiedDomains ?? [],
    ssoMode,
    ssoStatus: item.ssoStatus === "error" ? "error" : item.ssoStatus === "active" ? "active" : item.ssoStatus === "pending" ? "pending" : "not_configured",
    ssoEvidence: { checkedAt: null, result: "尚未验证，需去身份服务商完成。", connected: false },
    scimStatus: item.scimStatus === "error" ? "error" : item.scimStatus === "active" ? "active" : item.scimStatus === "pending" ? "pending" : "not_configured",
    loginPolicy: {
      passwordLoginEnabled: ssoMode !== "required",
      mfaRequired: ssoMode === "required",
      allowedDomainsOnly: ssoMode !== "disabled" && domains.length > 0,
    },
    loginPolicyStatus: ssoMode === "disabled" ? "ready" : "blocked",
    recoveryActions: [
      "前往身份服务商完成单点登录应用与域名验证。",
      "等待平台核验完成后，再对成员开放强制登录。",
      safeEvidenceText(item.lastError, "如保存失败，请检查企业域名和登录策略后重试。"),
    ],
    disableImpactPreview: [
      `${item.tenantName ?? "当前企业"} 的成员将不再通过企业单点登录进入控制面。`,
      "已登记域名会保留，但不会作为登录策略的强制依据。",
      "未取得外部验证证据的接入不会被标记为成功。",
    ],
    updatedAt: item.updatedAt ?? new Date(0).toISOString(),
  };
}

function isView(item: EnterpriseIdentityConfigView | LegacyIdentityConfig): item is EnterpriseIdentityConfigView {
  const record = recordOf(item);
  return Array.isArray(record.domains) && Boolean(record.ssoEvidence) && Boolean(record.loginPolicy) && Array.isArray(record.recoveryActions);
}

function normalizeIdentity(item: EnterpriseIdentityConfigView | LegacyIdentityConfig): EnterpriseIdentityConfigView {
  if (!isView(item)) return legacyIdentityToView(item);
  return {
    ...item,
    domains: arrayOf(item.domains).map(domainViewFromUnknown).filter((domain): domain is EnterpriseIdentityDomainView => Boolean(domain)),
    ssoEvidence: {
      checkedAt: item.ssoEvidence.checkedAt,
      result: safeEvidenceText(item.ssoEvidence.result, "尚未验证，需去身份服务商完成。"),
      connected: Boolean(item.ssoEvidence.connected && item.ssoEvidence.checkedAt),
    },
    recoveryActions: item.recoveryActions.map((action) => safeEvidenceText(action, "按平台恢复建议处理。")),
    disableImpactPreview: item.disableImpactPreview.map((impact) => safeEvidenceText(impact, "关闭后企业成员登录方式会变化。")),
  };
}

function normalizeTenant(item: TenantListItem): EnterpriseIdentityTenantOption {
  return {
    id: item.id,
    key: item.key ?? item.name,
    name: item.name,
    status: asTenantStatus(item.status),
    selectable: item.selectable ?? true,
    disabledReason: item.disabledReason ?? null,
  };
}

function normalizeConsole(identity: IdentityConsoleResponse, tenants: TenantListItem[]): EnterpriseIdentityConsole {
  const items = (identity.items ?? []).map(normalizeIdentity);
  const tenantOptions = (identity.tenantOptions?.length ? identity.tenantOptions : tenants.map(normalizeTenant)).map((tenant) => ({ ...tenant, status: asTenantStatus(tenant.status) }));
  const knownTenantIds = new Set(tenantOptions.map((tenant) => tenant.id));
  for (const item of items) {
    if (!knownTenantIds.has(item.tenantId)) {
      tenantOptions.push({ id: item.tenantId, key: item.tenantName, name: item.tenantName, status: "active", selectable: true, disabledReason: null });
    }
  }
  return { tenantOptions, items };
}

function rowsFromConsole(data: EnterpriseIdentityConsole): EnterpriseIdentityConfigView[] {
  const identityByTenant = new Map(data.items.map((item) => [item.tenantId, item]));
  return data.tenantOptions.map((tenant) => identityByTenant.get(tenant.id) ?? legacyIdentityToView({
    tenantId: tenant.id,
    tenantName: tenant.name,
    verifiedDomains: [],
    ssoMode: "disabled",
    ssoStatus: "not_configured",
    scimStatus: "not_configured",
  }));
}

function tenantSelectOptions(rows: EnterpriseIdentityConfigView[]) {
  return [
    { value: allTenantsValue, label: "全部企业" },
    ...rows.map((row) => ({ value: row.tenantId, label: row.tenantName })),
  ];
}

function draftFromRow(row: EnterpriseIdentityConfigView | null): IdentityDraft {
  return {
    tenantId: row?.tenantId ?? "",
    domains: row?.domains.map((domain) => domain.domain) ?? [],
    ssoMode: row?.ssoMode ?? "optional",
    loginPolicy: row?.loginPolicy ?? { passwordLoginEnabled: true, mfaRequired: false, allowedDomainsOnly: false },
    reason: defaultReason,
  };
}

function domainStatusBadge(item: EnterpriseIdentityConfigView): string {
  if (!item.domains.length) return "not_configured";
  if (item.domains.some((domain) => domain.status === "verification_failed")) return "failed";
  if (item.domains.every((domain) => domain.status === "verified" && domain.evidenceAt)) return "verified";
  return "unverified";
}

function ssoStatusBadge(item: EnterpriseIdentityConfigView): string {
  if (item.ssoMode === "disabled" || item.ssoStatus === "not_configured") return "not_configured";
  if (item.ssoStatus === "error") return "failed";
  if (item.ssoStatus === "active" && item.ssoEvidence.connected && item.ssoEvidence.checkedAt) return "verified";
  return "unverified";
}

function loginPolicyBadge(item: EnterpriseIdentityConfigView): string {
  if (item.loginPolicyStatus === "ready") return "ready";
  if (item.loginPolicyStatus === "blocked") return "failed";
  return "not_configured";
}

function domainStatusText(domain: EnterpriseIdentityDomainView): string {
  if (domain.status === "verified" && domain.evidenceAt) return "已验证";
  if (domain.status === "verification_failed") return "验证失败";
  return "尚未验证";
}

function domainEvidenceText(item: EnterpriseIdentityConfigView): string {
  if (!item.domains.length) return "尚未登记企业域名。";
  if (domainStatusBadge(item) === "verified") return "所有登记域名已通过平台核验。";
  const failed = item.domains.find((domain) => domain.status === "verification_failed");
  if (failed) return safeEvidenceText(failed.evidenceResult, "域名验证失败，请按恢复建议处理。");
  return "域名已登记，但尚未取得身份服务商验证证据。";
}

function ssoEvidenceText(item: EnterpriseIdentityConfigView): string {
  if (item.ssoMode === "disabled") return "未启用单点登录。";
  if (item.ssoStatus === "error") return safeEvidenceText(item.ssoEvidence.result, "单点登录验证失败，请按恢复建议处理。");
  if (item.ssoStatus === "active" && item.ssoEvidence.connected && item.ssoEvidence.checkedAt) return safeEvidenceText(item.ssoEvidence.result, "已收到身份服务商验证证据。");
  return "尚未验证，需去身份服务商完成配置与验证。";
}

function loginPolicyText(item: EnterpriseIdentityConfigView): string {
  if (item.loginPolicyStatus === "blocked") return "登录策略等待身份服务商证据，暂不建议全员强制。";
  const password = item.loginPolicy.passwordLoginEnabled ? "允许账号密码登录" : "关闭账号密码登录";
  const mfa = item.loginPolicy.mfaRequired ? "要求动态验证码" : "不强制动态验证码";
  const domainOnly = item.loginPolicy.allowedDomainsOnly ? "限制已登记域名成员" : "不限制企业域名";
  return `${password}，${mfa}，${domainOnly}。`;
}

function evidenceTime(item: EnterpriseIdentityConfigView): string {
  const ssoTime = item.ssoEvidence.checkedAt && (item.ssoEvidence.connected || item.ssoStatus === "error") ? item.ssoEvidence.checkedAt : null;
  const domainTime = item.domains.find((domain) => domain.evidenceAt && domain.status !== "pending_verification")?.evidenceAt;
  return formatDateTime(ssoTime ?? domainTime);
}

function policyForMode(mode: SsoMode, current: LoginPolicyDraft, domainCount: number): LoginPolicyDraft {
  if (mode === "disabled") return { ...current, passwordLoginEnabled: true, allowedDomainsOnly: false };
  if (mode === "required") return { ...current, mfaRequired: true, allowedDomainsOnly: domainCount > 0 };
  return { ...current, passwordLoginEnabled: true, allowedDomainsOnly: domainCount > 0 ? current.allowedDomainsOnly : false };
}

function loginPolicyPreviewText(draft: IdentityDraft): string {
  if (draft.ssoMode !== "disabled") return "保存后登录策略等待身份服务商证据，验证完成前不应按成功策略执行。";
  const password = draft.loginPolicy.passwordLoginEnabled ? "允许账号密码登录" : "关闭账号密码登录";
  const mfa = draft.loginPolicy.mfaRequired ? "要求动态验证码" : "不强制动态验证码";
  const domainOnly = draft.loginPolicy.allowedDomainsOnly ? "限制已登记域名成员" : "不限制企业域名";
  return `${password}，${mfa}，${domainOnly}。`;
}

function closeImpact(row: EnterpriseIdentityConfigView | null): string[] {
  return row?.disableImpactPreview.length ? row.disableImpactPreview : [
    `${row?.tenantName ?? "当前企业"} 的成员将不再通过企业单点登录进入控制面。`,
    "已登记域名会保留，但不会作为登录策略的强制依据。",
    "未取得外部验证证据的接入不会被标记为成功。",
  ];
}

function IdentityEvidenceCard({ title, badgeValue, evidence, time, failure, action }: {
  title: string;
  badgeValue: string;
  evidence: string;
  time: string;
  failure: string;
  action?: ReactNode;
}) {
  return <article className="enterprise-evidence-card">
    <header>
      <strong>{title}</strong>
      <StatusBadge value={badgeValue} />
    </header>
    <p>{evidence}</p>
    <span><Clock3 size={14} aria-hidden />证据时间：{time}</span>
    <small>失败原因：{failure}</small>
    {action}
  </article>;
}

export function EnterprisePage({ session }: { session: SessionView }) {
  const canManageEnterprise = hasAction(session, "enterprise.manage");
  const canReadTenants = hasAction(session, "tenant.read");
  const canManageApprovals = hasAction(session, "approvals.manage");
  const [activeTab, setActiveTab] = useState<EnterpriseTab>("identity");
  const loader = useCallback(async (): Promise<EnterpriseIdentityConsole> => {
    if (!canManageEnterprise) return { tenantOptions: [], items: [] };
    const [identity, tenants] = await Promise.all([
      api<IdentityConsoleResponse>("/api/enterprise/identity"),
      canReadTenants ? api<TenantResponse>("/api/tenants") : Promise.resolve({ items: [] as TenantListItem[] }),
    ]);
    return normalizeConsole(identity, tenants.items);
  }, [canManageEnterprise, canReadTenants]);
  const remote = useRemote(loader, (data) => rowsFromConsole(data).length === 0);
  const orgLoader = useCallback(async (): Promise<EnterpriseOrgUnitTreeResponse> => {
    if (!canManageEnterprise) return { items: [], memberCandidates: [] };
    return normalizeOrganizationConsole(await api<OrganizationConsoleResponse>("/api/enterprise/org-units"));
  }, [canManageEnterprise]);
  const orgRemote = useRemote(orgLoader, (data) => data.items.length === 0);
  const approvalLoader = useCallback(async (): Promise<ApprovalConsole> => {
    if (!canManageApprovals) return { operations: [], approvals: [] };
    const [directory, approvals] = await Promise.all([
      api<ApprovalOperationDirectoryResponse>("/api/enterprise/approval-operations"),
      api<{ items: ApprovalListItemView[] }>("/api/enterprise/approvals"),
    ]);
    return normalizeApprovalConsole(directory, approvals);
  }, [canManageApprovals]);
  const approvalRemote = useRemote(approvalLoader, (data) => data.operations.length === 0 && data.approvals.length === 0);
  const [selectedTenantId, setSelectedTenantId] = useState(allTenantsValue);
  const [editorRow, setEditorRow] = useState<EnterpriseIdentityConfigView | "new" | null>(null);
  const [recoveryRow, setRecoveryRow] = useState<EnterpriseIdentityConfigView | null>(null);
  const [orgUnitDialog, setOrgUnitDialog] = useState<OrgUnitDialogState | null>(null);
  const [orgTenantDialog, setOrgTenantDialog] = useState<OrgTenantDialogState>(null);
  const [orgMemberDialog, setOrgMemberDialog] = useState<OrgMemberDialogState>(null);
  const [orgRemoveMember, setOrgRemoveMember] = useState<OrgRemoveMemberState>(null);
  const [orgDelete, setOrgDelete] = useState<OrgDeleteState>(null);
  const [approvalWizardOpen, setApprovalWizardOpen] = useState(false);
  const [approvalDecision, setApprovalDecision] = useState<ApprovalDecisionState>(null);
  const identityRows = remote.state.status === "ready" || remote.state.status === "empty" ? rowsFromConsole(remote.state.data) : [];
  const orgData = orgRemote.state.status === "ready" || orgRemote.state.status === "empty" ? orgRemote.state.data : { items: [], memberCandidates: [] };
  const approvalData = approvalRemote.state.status === "ready" || approvalRemote.state.status === "empty" ? approvalRemote.state.data : { operations: [], approvals: [] };
  const orgFlatUnits = useMemo(() => flattenOrgUnits(orgData.items), [orgData.items]);
  const tabItems: V3PageTab[] = [
    { value: "identity", label: "身份接入", count: identityRows.length, panelId: "enterprise-identity-panel" },
    { value: "organization", label: "组织架构", count: orgFlatUnits.length, panelId: "enterprise-organization-panel" },
    {
      value: "approval",
      label: "审批中心",
      panelId: "enterprise-approval-panel",
      ...(canManageApprovals ? { count: approvalData.approvals.length } : { blocked: true, blockedLabel: "需权限" }),
    },
    { value: "audit", label: "审计证据", disabled: true, blocked: true, blockedLabel: "下一批恢复", panelId: "enterprise-audit-panel" },
  ];

  return <div className="page enterprise-page">
    <PageHeader
      icon={ShieldCheck}
      title="企业治理"
      meta={<span>身份接入、组织架构与审批均以平台核验结果展示；未完成核验不会显示为成功。</span>}
      actions={canManageEnterprise && activeTab === "identity" ? <button className="button primary" onClick={() => setEditorRow("new")}><KeyRound size={16} />配置身份接入</button> : undefined}
      navigation={<V3PageTabs tabs={tabItems} active={activeTab} onChange={(value) => setActiveTab(value as EnterpriseTab)} ariaLabel="企业治理页内视图" />}
    />
    {activeTab === "identity" && <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={!canManageEnterprise || (remote.state.status === "error" && remote.state.httpStatus === 403)
        ? { title: "当前账号不能管理企业身份", description: "请联系平台负责人开通企业治理权限；页面不会用空数据冒充真实配置。" }
        : null}
      emptyHint="当前还没有企业身份范围。请先完成企业创建，再配置域名与登录策略；保存后仍需到身份服务商完成验证。"
    >{(data) => {
      const rows = rowsFromConsole(data);
      const filteredRows = selectedTenantId === allTenantsValue ? rows : rows.filter((row) => row.tenantId === selectedTenantId);
      const selectedRow = selectedTenantId === allTenantsValue ? rows[0] ?? null : rows.find((row) => row.tenantId === selectedTenantId) ?? null;
      return <div className="workspace-stack enterprise-workbench" id="enterprise-identity-panel" role="tabpanel" aria-labelledby="v3-page-tab-identity">
        <SectionCard
          icon={Building2}
          title="企业身份工作台"
          meta={<span><strong className="app-number">{filteredRows.length}</strong> 个企业范围</span>}
          variant="ledger"
          filters={<div className="enterprise-toolbar">
            <div className="field-block enterprise-scope-select">
              <span>当前查看企业</span>
              <V3Select value={selectedTenantId} onChange={setSelectedTenantId} options={tenantSelectOptions(rows)} ariaLabel="当前查看企业" />
            </div>
          </div>}
          action={<button className="button secondary" onClick={() => setEditorRow("new")}><Globe2 size={16} />新增或更新域名</button>}
        >
          <div className="enterprise-evidence-grid">
            <IdentityEvidenceCard
              title="域名验证"
              badgeValue={selectedRow ? domainStatusBadge(selectedRow) : "not_configured"}
              evidence={selectedRow ? domainEvidenceText(selectedRow) : "请选择企业后查看域名验证状态。"}
              time={selectedRow ? evidenceTime(selectedRow) : "暂无证据时间"}
              failure={selectedRow ? safeEvidenceText(selectedRow.domains.find((domain) => domain.status === "verification_failed")?.evidenceResult, "暂无失败原因") : "暂无失败原因"}
            />
            <IdentityEvidenceCard
              title="单点登录"
              badgeValue={selectedRow ? ssoStatusBadge(selectedRow) : "not_configured"}
              evidence={selectedRow ? ssoEvidenceText(selectedRow) : "请选择企业后查看单点登录状态。"}
              time={selectedRow ? evidenceTime(selectedRow) : "暂无证据时间"}
              failure={selectedRow && selectedRow.ssoStatus === "error" ? safeEvidenceText(selectedRow.ssoEvidence.result, "失败原因待平台确认") : "暂无失败原因"}
              action={selectedRow ? <button type="button" className="button ghost" onClick={() => setRecoveryRow(selectedRow)}><RotateCcw size={15} />恢复建议</button> : undefined}
            />
            <IdentityEvidenceCard
              title="登录策略"
              badgeValue={selectedRow ? loginPolicyBadge(selectedRow) : "not_configured"}
              evidence={selectedRow ? loginPolicyText(selectedRow) : "请选择企业后查看登录策略。"}
              time={selectedRow ? evidenceTime(selectedRow) : "暂无证据时间"}
              failure={selectedRow?.loginPolicyStatus === "blocked" ? "缺少身份服务商验证证据" : "暂无失败原因"}
            />
          </div>
          <IdentityTable rows={filteredRows} onEdit={setEditorRow} onRecovery={setRecoveryRow} />
        </SectionCard>
        {editorRow && <IdentityAccessEditor
          rows={rows}
          initial={editorRow === "new" ? null : editorRow}
          onClose={() => setEditorRow(null)}
          onDone={() => { setEditorRow(null); void remote.reload(); }}
        />}
        {recoveryRow && <RecoveryDialog row={recoveryRow} onClose={() => setRecoveryRow(null)} />}
      </div>;
    }}</RemotePageState>}
    {activeTab === "organization" && <RemotePageState
      state={orgRemote.state}
      onRetry={orgRemote.reload}
      blocked={!canManageEnterprise || (orgRemote.state.status === "error" && orgRemote.state.httpStatus === 403)
        ? { title: "当前账号不能管理组织架构", description: "请联系平台负责人开通企业治理权限；页面不会用空组织冒充真实结构。" }
        : null}
      emptyHint="当前还没有组织架构。请先新增顶级组织，再把企业和成员分配到清晰的业务范围。"
      empty={<button type="button" className="button primary" onClick={() => setOrgUnitDialog({ mode: "create", parent: null })}><PlusCircle size={16} />新建第一个组织</button>}
    >{(data) => <OrganizationWorkbench
      data={data}
      tenantOptions={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.tenantOptions : []}
      onCreate={(parent) => setOrgUnitDialog({ mode: "create", parent })}
      onRename={(unit) => setOrgUnitDialog({ mode: "rename", unit })}
      onMove={(unit) => setOrgUnitDialog({ mode: "move", unit })}
      onAssignTenant={(unit) => setOrgTenantDialog({ unit })}
      onAssignMember={(unit) => setOrgMemberDialog({ unit })}
      onRemoveMember={(unit, member) => setOrgRemoveMember({ unit, member })}
      onDelete={(unit) => setOrgDelete({ unit })}
    />}</RemotePageState>}
    {activeTab === "approval" && <RemotePageState
      state={approvalRemote.state}
      onRetry={approvalRemote.reload}
      blocked={!canManageApprovals || (approvalRemote.state.status === "error" && approvalRemote.state.httpStatus === 403)
        ? { title: "当前账号不能管理审批", description: "请联系平台负责人开通审批权限；页面不会展示敏感审批目录。" }
        : null}
      emptyHint="当前没有可发起的审批动作或待处理申请。请确认当前角色范围后刷新。"
    >{(data) => <ApprovalCenter
      data={data}
      onCreate={() => setApprovalWizardOpen(true)}
      onDecide={(item, decision) => setApprovalDecision({ item, decision })}
    />}</RemotePageState>}
    {orgUnitDialog && <OrgUnitDialog
      state={orgUnitDialog}
      units={orgFlatUnits}
      onClose={() => setOrgUnitDialog(null)}
      onDone={() => orgRemote.reload()}
    />}
    {orgTenantDialog && <OrgTenantDialog
      state={orgTenantDialog}
      tenants={remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.tenantOptions : []}
      onClose={() => setOrgTenantDialog(null)}
      onDone={() => orgRemote.reload()}
    />}
    {orgMemberDialog && <OrgMemberDialog
      state={orgMemberDialog}
      memberCandidates={orgData.memberCandidates}
      onClose={() => setOrgMemberDialog(null)}
      onDone={() => orgRemote.reload()}
    />}
    {orgRemoveMember && <OrgRemoveMemberDialog
      state={orgRemoveMember}
      onClose={() => setOrgRemoveMember(null)}
      onDone={() => orgRemote.reload()}
    />}
    {orgDelete && <OrgDeleteDialog
      state={orgDelete}
      onClose={() => setOrgDelete(null)}
      onDone={() => orgRemote.reload()}
    />}
    {approvalWizardOpen && <ApprovalCreateWizard
      operations={approvalData.operations}
      onClose={() => setApprovalWizardOpen(false)}
      onDone={() => { setApprovalWizardOpen(false); void approvalRemote.reload(); }}
    />}
    {approvalDecision && <ApprovalDecisionDialog
      state={approvalDecision}
      onClose={() => setApprovalDecision(null)}
      onDone={() => { setApprovalDecision(null); void approvalRemote.reload(); }}
    />}
  </div>;
}

function IdentityTable({ rows, onEdit, onRecovery }: {
  rows: EnterpriseIdentityConfigView[];
  onEdit: (row: EnterpriseIdentityConfigView) => void;
  onRecovery: (row: EnterpriseIdentityConfigView) => void;
}) {
  const columns: Array<DataTableColumn<EnterpriseIdentityConfigView>> = [
    {
      key: "tenant",
      header: "企业范围",
      render: (row) => <span className="enterprise-cell-main"><strong>{row.tenantName}</strong><small>登录与域名配置只影响当前企业范围</small></span>,
      width: "18%",
    },
    {
      key: "domains",
      header: "登记域名",
      render: (row) => <DomainList row={row} />,
      width: "22%",
    },
    {
      key: "domainEvidence",
      header: "域名验证",
      render: (row) => <span className="enterprise-cell-main"><StatusBadge value={domainStatusBadge(row)} /><small>{domainEvidenceText(row)}</small><small>证据时间：{evidenceTime(row)}</small></span>,
      width: "20%",
    },
    {
      key: "sso",
      header: "单点登录",
      render: (row) => <span className="enterprise-cell-main"><StatusBadge value={ssoStatusBadge(row)} /><small>{ssoEvidenceText(row)}</small></span>,
      width: "20%",
    },
    {
      key: "policy",
      header: "登录策略",
      render: (row) => <span className="enterprise-cell-main"><StatusBadge value={loginPolicyBadge(row)} /><small>{loginPolicyText(row)}</small></span>,
      width: "20%",
    },
  ];
  return <DataTable
    ariaLabel="企业身份接入状态"
    columns={columns}
    rows={rows}
    rowKey={(row) => row.tenantId}
    empty={<span>暂无企业身份配置。请选择企业后登记域名，保存后仍需身份服务商完成验证。</span>}
    rowActions={(row) => <div className="row-action-group">
      <button type="button" className="button secondary" onClick={() => onRecovery(row)}><RotateCcw size={15} />恢复建议</button>
      <button type="button" className="button primary" onClick={() => onEdit(row)}><ShieldCheck size={15} />编辑接入</button>
    </div>}
  />;
}

function DomainList({ row }: { row: EnterpriseIdentityConfigView }) {
  if (!row.domains.length) return <span className="empty-inline">尚未登记域名</span>;
  return <span className="domain-chip-list">
    {row.domains.map((domain) => <span className={`domain-chip ${domainStatusBadge(row)}`} key={domain.domain}>
      <Globe2 size={13} aria-hidden />
      <span>{domain.domain}</span>
      <small>{domainStatusText(domain)}</small>
    </span>)}
  </span>;
}

function ApprovalCenter({ data, onCreate, onDecide }: {
  data: ApprovalConsole;
  onCreate: () => void;
  onDecide: (item: ApprovalListItemView, decision: "approved" | "rejected") => void;
}) {
  const operationColumns: Array<DataTableColumn<ApprovalDirectoryItemView>> = [
    { key: "operation", header: "业务动作", render: (item) => <span className="enterprise-cell-main"><strong>{item.label}</strong><small>{item.description}</small></span>, width: "32%" },
    { key: "category", header: "业务范围", render: (item) => approvalCategoryText(item.category), width: "18%" },
    { key: "target", header: "审批对象", render: (item) => item.targetLabel, width: "20%" },
    { key: "risk", header: "风险", render: (item) => <span className="enterprise-cell-main"><StatusBadge value={item.disabled ? "failed" : "pending"} /><small>{item.disabled ? businessCopy(item.unavailableReason, "暂不可发起") : "需要影响预览与双人确认"}</small></span>, width: "30%" },
  ];
  const approvalColumns: Array<DataTableColumn<ApprovalListItemView>> = [
    { key: "target", header: "申请对象", render: (item) => <span className="enterprise-cell-main"><strong>{item.operationLabel}</strong><small>{item.targetLabel}</small><small>{item.targetSummary}</small></span>, width: "30%" },
    { key: "status", header: "状态", render: (item) => <span className="enterprise-cell-main"><StatusBadge value={item.status} /><small>{approvalStatusText(item.status)}</small></span>, width: "16%" },
    { key: "requester", header: "申请人", render: (item) => <span className="enterprise-cell-main"><strong>{item.requestedByName}</strong><small>{formatDateTime(item.createdAt)}</small></span>, width: "20%" },
    { key: "impact", header: "影响与恢复", render: (item) => <span className="enterprise-cell-main"><small>{item.impact[0] ?? "影响范围待确认"}</small><small>{item.recovery}</small></span>, width: "34%" },
  ];
  return <div className="workspace-stack enterprise-approval-workbench" id="enterprise-approval-panel" role="tabpanel" aria-labelledby="v3-page-tab-approval">
    <SectionCard
      icon={ShieldAlert}
      title="审批中心"
      meta={<span><strong className="app-number">{data.operations.length}</strong> 类业务动作 · <strong className="app-number">{data.approvals.length}</strong> 条审批申请</span>}
      variant="ledger"
      action={<button type="button" className="button primary" onClick={onCreate}><PlusCircle size={16} />发起审批</button>}
    >
      <div className="enterprise-approval-layout">
        <section className="enterprise-approval-directory" aria-label="审批业务目录">
          <div className="enterprise-org-section-title">
            <strong>业务目录</strong>
            <span className="enterprise-org-action-hint">目录由当前角色范围决定，不可手动输入审批对象。</span>
          </div>
          <DataTable
            ariaLabel="审批业务目录"
            columns={operationColumns}
            rows={data.operations}
            rowKey={(item) => item.operationType}
            empty={<span>当前角色暂无可发起的审批动作。</span>}
          />
        </section>
        <section className="enterprise-approval-requests" aria-label="审批申请列表">
          <div className="enterprise-org-section-title">
            <strong>审批申请</strong>
            <span className="enterprise-org-action-hint">申请人不能审批自己的申请。</span>
          </div>
          <DataTable
            ariaLabel="审批申请列表"
            columns={approvalColumns}
            rows={data.approvals}
            rowKey={(item) => item.id}
            empty={<span>当前没有待处理审批申请。</span>}
            rowActions={(item) => <ApprovalRowActions item={item} onDecide={onDecide} />}
          />
        </section>
      </div>
    </SectionCard>
  </div>;
}

function ApprovalRowActions({ item, onDecide }: {
  item: ApprovalListItemView;
  onDecide: (item: ApprovalListItemView, decision: "approved" | "rejected") => void;
}) {
  const pending = item.status === "pending";
  if (!pending) return <span className="enterprise-approval-action-note">{approvalStatusText(item.status)}</span>;
  if (!item.canDecide) return <span className="enterprise-approval-self">
    <button type="button" className="button secondary" disabled>通过</button>
    <button type="button" className="button secondary" disabled>拒绝</button>
    <small>不能审批自己的申请</small>
  </span>;
  return <div className="row-action-group">
    <button type="button" className="button secondary" onClick={() => onDecide(item, "rejected")}><XCircle size={15} />拒绝</button>
    <button type="button" className="button primary" onClick={() => onDecide(item, "approved")}><CheckCircle2 size={15} />通过</button>
  </div>;
}

function ApprovalCreateWizard({ operations, onClose, onDone }: {
  operations: ApprovalDirectoryItemView[];
  onClose: () => void;
  onDone: () => void;
}) {
  const firstOperation = operations.find((item) => !item.disabled) ?? null;
  const [draft, setDraft] = useState<ApprovalDraft>(() => ({
    operationType: firstOperation?.operationType ?? "",
    targetValue: "",
    targetOptions: [],
    targetLoading: false,
    targetError: "",
    payload: firstOperation ? defaultApprovalPayload(firstOperation.operationType, firstOperation.inputFields ?? []) : {},
    reason: "",
    preview: null,
    previewLoading: false,
    previewError: "",
    submitting: false,
    submitError: "",
  }));
  const operation = operations.find((item) => item.operationType === draft.operationType) ?? null;
  const fields = operation?.inputFields ?? [];
  const targetOptions = approvalTargetOptions(draft.targetOptions);

  useEffect(() => {
    if (!draft.operationType) return;
    const nextOperation = operations.find((item) => item.operationType === draft.operationType);
    let cancelled = false;
    setDraft((value) => ({
      ...value,
      targetValue: "",
      targetOptions: [],
      targetLoading: true,
      targetError: "",
      payload: nextOperation ? defaultApprovalPayload(nextOperation.operationType, nextOperation.inputFields ?? []) : {},
      preview: null,
      previewError: "",
      submitError: "",
    }));
    api<ApprovalTargetOptionsResponse>(`/api/enterprise/approval-targets?operationType=${encodeURIComponent(draft.operationType)}`)
      .then((response) => {
        if (cancelled) return;
        const options = Array.isArray(response.items) ? response.items : [];
        const firstTarget = options.find((item) => !item.disabled)?.selectionToken ?? "";
        setDraft((value) => ({ ...value, targetOptions: options, targetValue: firstTarget, targetLoading: false }));
      })
      .catch((failure) => {
        if (cancelled) return;
        setDraft((value) => ({ ...value, targetLoading: false, targetError: failure instanceof Error ? failure.message : "审批对象加载失败，请刷新后重试。" }));
      });
    return () => { cancelled = true; };
  }, [draft.operationType, operations]);

  function chooseOperation(value: string) {
    setDraft((current) => ({ ...current, operationType: value as ApprovalOperationType, preview: null, previewError: "", submitError: "" }));
  }

  function chooseTarget(value: string) {
    setDraft((current) => ({ ...current, targetValue: value, preview: null, previewError: "", submitError: "" }));
  }

  function setPayloadValue(key: string, value: unknown) {
    setDraft((current) => ({ ...current, payload: { ...current.payload, [key]: value }, preview: null, previewError: "", submitError: "" }));
  }

  function validateOperation(): string | null {
    if (!draft.operationType || !operation) return "请选择要发起审批的业务动作。";
    if (operation.disabled) return businessCopy(operation.unavailableReason, "当前业务动作暂不可发起。");
    return null;
  }

  function validateInputs(): string | null {
    if (!draft.targetValue) return draft.targetLoading ? "审批对象仍在加载，请稍后。" : "请选择审批对象。";
    for (const field of fields) {
      const value = draft.payload[field.key];
      if (!field.required) continue;
      if ((field.type === "boolean" || field.type === "checkbox") && value !== true) return `请确认「${field.label}」。`;
      if (field.type !== "boolean" && field.type !== "checkbox" && (value === undefined || value === null || String(value).trim() === "")) return `请填写「${field.label}」。`;
    }
    if (draft.reason.trim().length < 12) return "申请原因至少需要 12 个字，请说明业务背景和影响范围。";
    return null;
  }

  async function generatePreview() {
    if (draft.previewLoading || draft.submitting) return;
    const failure = validateOperation() ?? validateInputs();
    if (failure || !draft.operationType) { setDraft((value) => ({ ...value, previewError: failure ?? "请补齐审批信息。" })); return; }
    setDraft((value) => ({ ...value, previewLoading: true, previewError: "", submitError: "" }));
    try {
      const preview = await api<ApprovalPreviewResponse>("/api/enterprise/approvals/preview", {
        method: "POST",
        body: JSON.stringify({
          operationType: draft.operationType,
          target: approvalTargetFor(draft.operationType, draft.targetValue),
          payload: draft.payload,
        } satisfies EnterpriseApprovalPreviewInput),
      });
      setDraft((value) => ({ ...value, preview, previewLoading: false, payload: preview.canonicalBusinessInputs, previewError: "" }));
    } catch (failure) {
      setDraft((value) => ({ ...value, previewLoading: false, previewError: failure instanceof Error ? failure.message : "影响预览生成失败，请检查后重试。" }));
    }
  }

  async function submitApproval() {
    if (draft.submitting) return;
    if (!draft.operationType || !draft.preview) { setDraft((value) => ({ ...value, submitError: "请先生成影响预览。" })); return; }
    setDraft((value) => ({ ...value, submitting: true, submitError: "" }));
    try {
      const body: EnterpriseApprovalCreateInput = {
        operationType: draft.operationType,
        target: approvalTargetFor(draft.operationType, draft.targetValue),
        reason: draft.reason.trim(),
        payload: draft.preview.canonicalBusinessInputs,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        idempotencyKey: draft.preview.idempotencyKey,
      };
      await api("/api/enterprise/approvals", { method: "POST", body: JSON.stringify(body) });
      onDone();
    } catch (failure) {
      setDraft((value) => ({ ...value, submitting: false, submitError: failure instanceof Error ? failure.message : "审批申请提交失败，请稍后重试。" }));
    }
  }

  const steps: WizardStep[] = [
    {
      key: "operation",
      title: "选择动作",
      summary: "先选择要发起审批的业务动作，后续对象和输入会按目录自动收敛。",
      validate: validateOperation,
      content: <div className="approval-operation-step" role="radiogroup" aria-label="业务动作">
        {operations.map((item) => {
          const selected = item.operationType === draft.operationType;
          return <button
            key={item.operationType}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`approval-operation-option ${selected ? "selected" : ""}`}
            disabled={item.disabled}
            onClick={() => chooseOperation(item.operationType)}
          >
            <span className="approval-operation-option-head">
              <strong>{item.label}</strong>
              {selected && <CheckCircle2 size={17} aria-hidden />}
            </span>
            <span>{approvalCategoryText(item.category)} · {item.targetLabel}</span>
            <small>{item.disabled ? businessCopy(item.unavailableReason, "当前暂不可发起") : item.description}</small>
          </button>;
        })}
      </div>,
    },
    {
      key: "target",
      title: "补充信息",
      summary: "只能从平台给出的业务对象中选择，并填写目录要求的业务字段。",
      validate: validateInputs,
      content: <div className="enterprise-approval-form">
        <div className="field-block full">
          <span>业务对象</span>
          <V3Select value={draft.targetValue} onChange={chooseTarget} options={targetOptions} ariaLabel="业务对象" placeholder={draft.targetLoading ? "正在加载业务对象…" : "请选择业务对象"} disabled={draft.targetLoading || targetOptions.length === 0} />
          {draft.targetError && <InlineFeedback tone="danger">{draft.targetError}</InlineFeedback>}
          {!draft.targetLoading && !draft.targetError && targetOptions.length === 0 && <InlineFeedback tone="warning">当前没有可选择的业务对象，请刷新目录或更换业务动作。</InlineFeedback>}
        </div>
        {fields.map((field) => <ApprovalInputField key={field.key} field={field} value={draft.payload[field.key]} onChange={(value) => setPayloadValue(field.key, value)} />)}
        <label className="full">
          <span>申请原因</span>
          <textarea aria-label="申请原因" value={draft.reason} onChange={(event) => setDraft((value) => ({ ...value, reason: event.target.value, preview: null, submitError: "" }))} rows={4} placeholder={defaultApprovalReason} />
        </label>
      </div>,
    },
    {
      key: "preview",
      title: "确认影响",
      summary: "提交前必须生成影响预览；未生成预览时不能提交。",
      content: <div className="enterprise-approval-form">
        <button type="button" className="button secondary" onClick={() => void generatePreview()} disabled={draft.previewLoading || draft.submitting}>
          <FileCheck2 size={16} />{draft.previewLoading ? "正在生成预览…" : "生成影响预览"}
        </button>
        {draft.previewError && <InlineFeedback tone="danger">{draft.previewError}</InlineFeedback>}
        {draft.preview ? <ApprovalPreviewPanel preview={draft.preview} /> : <div className="approval-summary-card muted">
          <strong>等待影响预览</strong>
          <p>预览生成前不会提交审批申请，也不会显示为已完成。</p>
        </div>}
      </div>,
    },
  ];

  return <Wizard
    className="approval-wizard-dialog"
    title="发起审批"
    description="从业务目录选择动作、对象和影响预览后再提交申请。"
    steps={steps}
    submitLabel="提交审批申请"
    onSubmit={submitApproval}
    onClose={onClose}
    submitting={draft.submitting}
    submitDisabled={!draft.preview || draft.previewLoading || draft.submitting}
    submitError={draft.submitError}
  />;
}

function ApprovalInputField({ field, value, onChange }: {
  field: ApprovalInputFieldView;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (field.type === "fixed") {
    return <div className="approval-fixed-field full">
      <span>{field.label}</span>
      <strong>{businessCopy(fieldValueAsString(value ?? field.value), "已由当前业务对象确定")}</strong>
      {field.helperText && <small>{field.helperText}</small>}
    </div>;
  }
  if (field.type === "select") {
    const options = (field.options ?? []).map((option) => ({
      value: String(option.value),
      label: option.label,
      ...((option.description ?? option.summary) ? { hint: option.description ?? option.summary } : {}),
    }));
    return <div className="field-block full">
      <span>{field.label}</span>
      <V3Select
        value={fieldValueAsString(value)}
        onChange={(next) => {
          const selected = field.options?.find((option) => String(option.value) === next);
          onChange(selected?.value ?? next);
        }}
        options={options}
        ariaLabel={field.label}
        placeholder={`请选择${field.label}`}
      />
      {field.helperText && <span className="field-hint">{field.helperText}</span>}
    </div>;
  }
  if (field.type === "boolean" || field.type === "checkbox") {
    return <label className="approval-check-row full">
      <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
      <span>
        <strong>{field.label}</strong>
        {field.helperText && <small>{field.helperText}</small>}
      </span>
    </label>;
  }
  const textarea = field.type === "textarea" || field.type === "secret_digest";
  return <label className="full">
    <span>{field.label}</span>
    {textarea
      ? <textarea value={fieldValueAsString(value)} onChange={(event) => onChange(event.target.value)} rows={3} readOnly={field.readonly} />
      : <input value={fieldValueAsString(value)} onChange={(event) => onChange(event.target.value)} readOnly={field.readonly} />}
    {field.helperText && <span className="field-hint">{field.helperText}</span>}
  </label>;
}

function ApprovalPreviewPanel({ preview }: { preview: ApprovalPreviewResponse }) {
  return <div className="approval-preview-panel">
    <div className="approval-summary-card">
      <strong>{businessCopy(preview.target.label, "业务对象")}</strong>
      <span>{businessCopy(preview.target.summary, "影响范围待确认")}</span>
    </div>
    <div>
      <strong>影响范围</strong>
      <ul className="impact-list">
        {preview.impact.map((item) => <li key={item}>{businessCopy(item, "影响范围待确认")}</li>)}
      </ul>
    </div>
    <InlineFeedback tone="warning">恢复方式：{businessCopy(preview.recovery, "按恢复清单重新发起审批。")}</InlineFeedback>
  </div>;
}

function ApprovalDecisionDialog({ state, onClose, onDone }: {
  state: NonNullable<ApprovalDecisionState>;
  onClose: () => void;
  onDone: () => void;
}) {
  const approve = state.decision === "approved";
  const [reason, setReason] = useState(approve ? "审批影响已确认，同意执行。" : "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function executeDecision() {
    if (!approve && reason.trim().length < 8) { setError("拒绝原因至少需要 8 个字。"); return; }
    setBusy(true);
    setError("");
    try {
      await api(`/api/approvals/${state.item.id}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision: state.decision, reason: reason.trim() }),
      });
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "审批处理失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <ConfirmDialog
    title={approve ? "确认通过审批" : "确认拒绝审批"}
    description={`${state.item.operationLabel} · ${state.item.targetLabel}`}
    confirmLabel={approve ? "确认通过" : "确认拒绝"}
    tone={approve ? "primary" : "danger"}
    busy={busy}
    confirmDisabled={!approve && reason.trim().length < 8}
    onClose={onClose}
    onConfirm={() => void executeDecision()}
  >
    <div className="approval-decision-body">
      <span>{state.item.targetSummary}</span>
      <ul className="impact-list">
        {state.item.impact.map((item) => <li key={item}>{item}</li>)}
      </ul>
      {!approve && <label className="confirm-reason-field">
        <span>拒绝原因</span>
        <textarea aria-label="拒绝原因" value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
      </label>}
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
    </div>
  </ConfirmDialog>;
}

function OrganizationWorkbench({ data, tenantOptions, onCreate, onRename, onMove, onAssignTenant, onAssignMember, onRemoveMember, onDelete }: {
  data: EnterpriseOrgUnitTreeResponse;
  tenantOptions: EnterpriseIdentityTenantOption[];
  onCreate: (parent: EnterpriseOrgUnitTreeNode | null) => void;
  onRename: (unit: EnterpriseOrgUnitTreeNode) => void;
  onMove: (unit: EnterpriseOrgUnitTreeNode) => void;
  onAssignTenant: (unit: EnterpriseOrgUnitTreeNode) => void;
  onAssignMember: (unit: EnterpriseOrgUnitTreeNode) => void;
  onRemoveMember: (unit: EnterpriseOrgUnitTreeNode, member: EnterpriseOrgUnitMember) => void;
  onDelete: (unit: EnterpriseOrgUnitTreeNode) => void;
}) {
  const flatUnits = useMemo(() => flattenOrgUnits(data.items), [data.items]);
  const [selectedUnitId, setSelectedUnitId] = useState(flatUnits[0]?.unit.id ?? "");
  const selectedUnit = flatUnits.find((item) => item.unit.id === selectedUnitId)?.unit ?? flatUnits[0]?.unit ?? null;
  const assignedTenantCount = flatUnits.reduce((sum, item) => sum + item.unit.directTenantCount, 0);
  const memberCount = flatUnits.reduce((sum, item) => sum + item.unit.directMemberCount, 0);
  const tenantActionDisabled = tenantOptions.length === 0;
  return <div className="workspace-stack enterprise-org-workbench" id="enterprise-organization-panel" role="tabpanel" aria-labelledby="v3-page-tab-organization">
    <SectionCard
      icon={GitBranch}
      title="组织架构"
      meta={<span><strong className="app-number">{flatUnits.length}</strong> 个组织 · <strong className="app-number">{assignedTenantCount}</strong> 个企业归属 · <strong className="app-number">{memberCount}</strong> 名成员</span>}
      variant="ledger"
      action={<button type="button" className="button primary" onClick={() => onCreate(null)}><PlusCircle size={16} />新增顶级组织</button>}
    >
      <div className="enterprise-org-layout">
        <section className="enterprise-org-tree-panel" aria-label="组织树">
          <header>
            <strong>组织树</strong>
            <span>按业务层级选择节点</span>
          </header>
          <ul className="enterprise-org-tree">
            {data.items.map((unit) => <OrgTreeNode key={unit.id} unit={unit} selectedUnitId={selectedUnit?.id ?? ""} onSelect={setSelectedUnitId} />)}
          </ul>
        </section>
        <section className="enterprise-org-detail" aria-label="组织详情">
          {selectedUnit ? <OrgUnitDetail
            unit={selectedUnit}
            tenantActionDisabled={tenantActionDisabled}
            tenantActionHint={tenantActionDisabled ? "暂时没有可分配企业；请确认企业列表已加载。" : ""}
            onCreate={onCreate}
            onRename={onRename}
            onMove={onMove}
            onAssignTenant={onAssignTenant}
            onAssignMember={onAssignMember}
            onRemoveMember={onRemoveMember}
            onDelete={onDelete}
          /> : <div className="enterprise-org-empty">
            <strong>请选择组织节点</strong>
            <span>如果组织树为空，请新增顶级组织后再分配企业和成员。</span>
            <button type="button" className="button primary" onClick={() => onCreate(null)}><PlusCircle size={16} />新增顶级组织</button>
          </div>}
        </section>
      </div>
    </SectionCard>
  </div>;
}

function OrgTreeNode({ unit, selectedUnitId, onSelect }: {
  unit: EnterpriseOrgUnitTreeNode;
  selectedUnitId: string;
  onSelect: (id: string) => void;
}) {
  const selected = unit.id === selectedUnitId;
  return <li>
    <button
      type="button"
      className={`enterprise-org-tree-node ${selected ? "active" : ""}`.trim()}
      onClick={() => onSelect(unit.id)}
      aria-current={selected ? "true" : undefined}
      style={{ paddingLeft: `${12 + unit.depth * 14}px` }}
    >
      <span className="enterprise-org-tree-title">
        <strong>{unit.name}</strong>
        <small>{orgPathLabel(unit)}</small>
      </span>
      <span className="enterprise-org-tree-counts">
        <span><UsersRound size={13} aria-hidden /><strong className="app-number">{unit.directMemberCount}</strong> 人</span>
        <span><Building2 size={13} aria-hidden /><strong className="app-number">{unit.subtreeTenantCount}</strong> 企</span>
      </span>
    </button>
    {unit.children.length > 0 && <ul>{unit.children.map((child) => <OrgTreeNode key={child.id} unit={child} selectedUnitId={selectedUnitId} onSelect={onSelect} />)}</ul>}
  </li>;
}

function OrgUnitDetail({ unit, tenantActionDisabled, tenantActionHint, onCreate, onRename, onMove, onAssignTenant, onAssignMember, onRemoveMember, onDelete }: {
  unit: EnterpriseOrgUnitTreeNode;
  tenantActionDisabled: boolean;
  tenantActionHint: string;
  onCreate: (parent: EnterpriseOrgUnitTreeNode | null) => void;
  onRename: (unit: EnterpriseOrgUnitTreeNode) => void;
  onMove: (unit: EnterpriseOrgUnitTreeNode) => void;
  onAssignTenant: (unit: EnterpriseOrgUnitTreeNode) => void;
  onAssignMember: (unit: EnterpriseOrgUnitTreeNode) => void;
  onRemoveMember: (unit: EnterpriseOrgUnitTreeNode, member: EnterpriseOrgUnitMember) => void;
  onDelete: (unit: EnterpriseOrgUnitTreeNode) => void;
}) {
  const deleteDisabledReason = unit.canDelete ? "" : unit.blockers.join("；") || "当前组织暂不可删除。";
  return <div className="enterprise-org-detail-stack">
    <div className="enterprise-org-detail-head">
      <div>
        <span className="eyebrow">当前组织</span>
        <h3>{unit.name}</h3>
        <p>{orgPathLabel(unit)}</p>
      </div>
      <div className="enterprise-org-actions">
        <button type="button" className="button secondary" onClick={() => onCreate(unit)}><PlusCircle size={15} />新增下级</button>
        <button type="button" className="button secondary" onClick={() => onRename(unit)}><Pencil size={15} />改名</button>
        <button type="button" className="button secondary" onClick={() => onMove(unit)} disabled={!unit.canMove} title={!unit.canMove ? "当前组织暂不可移动" : undefined}><MoveRight size={15} />移动</button>
        <button type="button" className="button ghost danger" onClick={() => onDelete(unit)} disabled={!unit.canDelete} title={deleteDisabledReason}><Trash2 size={15} />删除</button>
      </div>
    </div>
    <div className="enterprise-org-summary-grid">
      <span><strong className="app-number">{unit.directMemberCount}</strong><small>直属成员</small></span>
      <span><strong className="app-number">{unit.directTenantCount}</strong><small>直属企业</small></span>
      <span><strong className="app-number">{unit.subtreeTenantCount}</strong><small>子树企业</small></span>
      <span><strong className="app-number">{unit.depth + 1}</strong><small>所在层级</small></span>
    </div>
    {unit.blockers.length > 0 && <InlineFeedback tone="warning">当前节点存在阻断：{unit.blockers.join("；")}。不可用操作已停用，请先清理对应归属。</InlineFeedback>}
    <div className="enterprise-org-scope">
      <div className="enterprise-org-section-title">
        <strong>业务名称范围</strong>
        <button type="button" className="button secondary" onClick={() => onAssignTenant(unit)} disabled={tenantActionDisabled} title={tenantActionHint}><Link2 size={15} />分配企业</button>
      </div>
      {tenantActionDisabled && <small className="enterprise-org-action-hint">{tenantActionHint}</small>}
      <TenantScopeList scope={unit.tenantScope} />
    </div>
    <div className="enterprise-org-members">
      <div className="enterprise-org-section-title">
        <strong>成员</strong>
        <button type="button" className="button secondary" onClick={() => onAssignMember(unit)}><UserPlus size={15} />添加成员</button>
      </div>
      <OrgMembersTable unit={unit} onRemoveMember={onRemoveMember} />
    </div>
  </div>;
}

function TenantScopeList({ scope }: { scope: EnterpriseOrgUnitTenantScope[] }) {
  if (!scope.length) return <div className="enterprise-org-empty-inline">暂无企业归属。分配企业后，这里只显示企业名称和业务状态。</div>;
  return <div className="enterprise-org-tenant-list">
    {scope.map((tenant) => <span className="enterprise-org-tenant-chip" key={tenant.tenantId}>
      <Building2 size={14} aria-hidden />
      <strong>{tenant.tenantName}</strong>
      <StatusBadge value={tenant.tenantStatus} />
    </span>)}
  </div>;
}

function OrgMembersTable({ unit, onRemoveMember }: {
  unit: EnterpriseOrgUnitTreeNode;
  onRemoveMember: (unit: EnterpriseOrgUnitTreeNode, member: EnterpriseOrgUnitMember) => void;
}) {
  const columns: Array<DataTableColumn<EnterpriseOrgUnitMember>> = [
    { key: "member", header: "成员", render: (member) => <span className="enterprise-cell-main"><strong>{member.displayName}</strong><small>{member.email}</small></span>, width: "35%" },
    { key: "role", header: "平台角色", render: (member) => roleLabels[member.role], width: "24%" },
    { key: "status", header: "状态", render: (member) => <span className="enterprise-cell-main"><StatusBadge value={member.status} /><small>{adminStatusLabels[member.status]}</small></span>, width: "20%" },
    { key: "scope", header: "组织范围", render: () => <span>{unit.name}</span>, width: "21%" },
  ];
  return <DataTable
    ariaLabel={`${unit.name} 成员`}
    columns={columns}
    rows={unit.members}
    rowKey={(member) => member.adminId}
    empty={<span>暂无直属成员。添加成员后将成为当前组织的直属成员。</span>}
    rowActions={(member) => <button type="button" className="button secondary" onClick={() => onRemoveMember(unit, member)}><UserMinus size={15} />移除成员</button>}
  />;
}

function OrgUnitDialog({ state, units, onClose, onDone }: {
  state: OrgUnitDialogState;
  units: OrgUnitFlat[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const isCreate = state.mode === "create";
  const isRename = state.mode === "rename";
  const parentOptions = isRename ? [] : isCreate ? unitParentOptions(units) : unitMoveOptions(units, state.unit);
  const originalParentValue = state.mode === "move" ? state.unit.parentId ?? rootParentValue : rootParentValue;
  const [name, setName] = useState(state.mode === "rename" ? state.unit.name : "");
  const [parentId, setParentId] = useState(isCreate ? state.parent?.id ?? rootParentValue : originalParentValue);
  const [reason, setReason] = useState(defaultOrgReason);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const title = isCreate ? "新增组织" : isRename ? "组织改名" : "移动组织";
  const submitLabel = isCreate ? "创建组织" : isRename ? "保存名称" : "保存移动";

  function validate(): string | null {
    if ((isCreate || isRename) && name.trim().length < 2) return "组织名称至少需要 2 个字符。";
    if (!isRename && !parentId) return "请选择上级组织。";
    if (state.mode === "move" && parentId === originalParentValue) return "请选择一个新的上级组织。";
    if (!canUseReason(reason)) return "请填写至少 8 个字符的调整原因。";
    return null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const failure = validate();
    if (failure) { setError(failure); return; }
    setBusy(true);
    setError("");
    try {
      if (state.mode === "create") {
        await api("/api/enterprise/org-units", {
          method: "POST",
          body: JSON.stringify({ name: name.trim(), parentId: parentId === rootParentValue ? null : parentId, reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("create") }),
        });
      } else if (state.mode === "rename") {
        await api(`/api/enterprise/org-units/${state.unit.id}`, {
          method: "PATCH",
          body: JSON.stringify({ name: name.trim(), reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("rename") }),
        });
      } else {
        await api(`/api/enterprise/org-units/${state.unit.id}`, {
          method: "PATCH",
          body: JSON.stringify({ parentId: parentId === rootParentValue ? null : parentId, reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("move") }),
        });
      }
      await onDone();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "组织调整失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <Dialog title={title} description="组织调整会写入审计链；保存后刷新查看最新组织架构。" onClose={onClose} wide>
    <form className="form-grid enterprise-org-form" onSubmit={submit}>
      {(isCreate || isRename) && <label className="full">
        <span>组织名称</span>
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder="例如 华东直营网点" required />
        <span className="field-hint">只填写业务名称，系统会自动处理编号。</span>
      </label>}
      {!isRename && <div className="field-block full">
        <span>上级组织</span>
        <V3Select value={parentId} onChange={setParentId} options={parentOptions} ariaLabel="上级组织" />
        <span className="field-hint">{state.mode === "move" ? "不能移动到自己或自己的下级组织。" : "新增下级时系统会自动记录层级路径。"}</span>
      </div>}
      <label className="full">
        <span>调整原因</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} required />
      </label>
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy}>{busy ? "正在保存…" : submitLabel}</button>
      </div>
    </form>
  </Dialog>;
}

function OrgTenantDialog({ state, tenants, onClose, onDone }: {
  state: NonNullable<OrgTenantDialogState>;
  tenants: EnterpriseIdentityTenantOption[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const options = selectableTenantOptions(tenants, state.unit);
  const [tenantId, setTenantId] = useState(firstEnabled(options));
  const [reason, setReason] = useState(defaultOrgReason);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!tenantId) { setError("当前没有可分配企业，请先确认企业列表。"); return; }
    if (!canUseReason(reason)) { setError("请填写至少 8 个字符的调整原因。"); return; }
    setBusy(true);
    setError("");
    try {
      await api(`/api/enterprise/org-units/${state.unit.id}/tenants/${tenantId}`, {
        method: "PUT",
        body: JSON.stringify({ reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("assign-tenant") }),
      });
      await onDone();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "企业分配失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <Dialog title="分配企业到组织" description={`当前组织：${state.unit.name}。只显示企业名称，系统会自动关联当前选择。`} onClose={onClose}>
    <form className="form-grid enterprise-org-form" onSubmit={submit}>
      <div className="field-block full">
        <span>企业</span>
        <V3Select value={tenantId} onChange={setTenantId} options={options} ariaLabel="企业" placeholder="暂无可分配企业" disabled={options.length === 0} />
        <span className="field-hint">分配企业只改变组织归属，不会改变租户启用状态。</span>
      </div>
      <label className="full">
        <span>调整原因</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} required />
      </label>
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy || !tenantId}>{busy ? "正在分配…" : "保存企业归属"}</button>
      </div>
    </form>
  </Dialog>;
}

function OrgMemberDialog({ state, memberCandidates, onClose, onDone }: {
  state: NonNullable<OrgMemberDialogState>;
  memberCandidates: EnterpriseOrgUnitMemberCandidate[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const currentMemberIds = new Set(state.unit.members.map((member) => member.adminId));
  const options = memberCandidates.map((member) => ({
    value: member.adminId,
    label: `${member.displayName} · ${roleLabels[member.role]}`,
    hint: currentMemberIds.has(member.adminId) ? "已在当前组织" : `${member.email} · ${adminStatusLabels[member.status]}`,
    disabled: currentMemberIds.has(member.adminId) || member.status !== "active",
  }));
  const [adminId, setAdminId] = useState(firstEnabled(options));
  const [reason, setReason] = useState(defaultOrgReason);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!adminId) { setError("当前没有可添加成员，请先确认管理员列表。"); return; }
    if (!canUseReason(reason)) { setError("请填写至少 8 个字符的调整原因。"); return; }
    setBusy(true);
    setError("");
    try {
      await api(`/api/enterprise/org-units/${state.unit.id}/members/${adminId}`, {
        method: "PUT",
        body: JSON.stringify({ reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("assign-member") }),
      });
      await onDone();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "成员添加失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <Dialog title="添加组织成员" description={`当前组织：${state.unit.name}。成员权限仍由当前角色范围控制。`} onClose={onClose}>
    <form className="form-grid enterprise-org-form" onSubmit={submit}>
      <div className="field-block full">
        <span>成员</span>
        <V3Select value={adminId} onChange={setAdminId} options={options} ariaLabel="成员" placeholder="暂无可添加成员" disabled={options.length === 0} />
        <span className="field-hint">列表只显示姓名、邮箱和角色，无需输入任何编号。</span>
      </div>
      <label className="full">
        <span>调整原因</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} required />
      </label>
      {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
      <div className="form-actions full">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
        <button className="button primary" disabled={busy || !adminId}>{busy ? "正在添加…" : "保存成员归属"}</button>
      </div>
    </form>
  </Dialog>;
}

function OrgRemoveMemberDialog({ state, onClose, onDone }: {
  state: NonNullable<OrgRemoveMemberState>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState(defaultOrgReason);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function executeRemoveMember() {
    if (!canUseReason(reason)) { setError("请填写至少 8 个字符的调整原因。"); return; }
    setBusy(true);
    setError("");
    try {
      await api(`/api/enterprise/org-units/${state.unit.id}/members/${state.member.adminId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("remove-member") }),
      });
      await onDone();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "成员移除失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <ConfirmDialog
    title="移除组织成员"
    description={`${state.member.displayName} 将不再归属 ${state.unit.name}，平台角色本身不会被删除。`}
    confirmLabel="确认移除成员"
    tone="danger"
    busy={busy}
    confirmDisabled={!canUseReason(reason)}
    onClose={onClose}
    onConfirm={() => void executeRemoveMember()}
  >
    <label className="confirm-reason-field">
      <span>调整原因</span>
      <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
    </label>
    {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
  </ConfirmDialog>;
}

function OrgDeleteDialog({ state, onClose, onDone }: {
  state: NonNullable<OrgDeleteState>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState(defaultOrgReason);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const blockers = state.unit.blockers.length ? state.unit.blockers : ["当前暂不可删除"];

  async function executeDeleteUnit() {
    if (!state.unit.canDelete) { setError(`当前不能删除：${blockers.join("；")}`); return; }
    if (!canUseReason(reason)) { setError("请填写至少 8 个字符的删除原因。"); return; }
    setBusy(true);
    setError("");
    try {
      await api(`/api/enterprise/org-units/${state.unit.id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason: reason.trim(), idempotencyKey: createOrgIdempotencyKey("delete") }),
      });
      await onDone();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "组织删除失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return <ConfirmDialog
    title="删除组织前确认"
    description={`${state.unit.name} 删除后不可在页面恢复；有企业、成员或下级时平台会阻止删除。`}
    confirmLabel="确认删除组织"
    tone="danger"
    busy={busy}
    confirmDisabled={!state.unit.canDelete || !canUseReason(reason)}
    onClose={onClose}
    onConfirm={() => void executeDeleteUnit()}
  >
    {!state.unit.canDelete && <InlineFeedback tone="warning">当前不能删除：{blockers.join("；")}。</InlineFeedback>}
    <label className="confirm-reason-field">
      <span>删除原因</span>
      <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
    </label>
    {error && <InlineFeedback tone="danger">{error}</InlineFeedback>}
  </ConfirmDialog>;
}

function IdentityAccessEditor({ rows, initial, onClose, onDone }: {
  rows: EnterpriseIdentityConfigView[];
  initial: EnterpriseIdentityConfigView | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const firstRow = initial ?? rows[0] ?? null;
  const [draft, setDraft] = useState<IdentityDraft>(() => draftFromRow(firstRow));
  const [domainInput, setDomainInput] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const currentRow = rows.find((row) => row.tenantId === draft.tenantId) ?? null;
  const tenantOptions = rows.map((row) => ({ value: row.tenantId, label: row.tenantName }));

  function chooseTenant(value: string) {
    const row = rows.find((item) => item.tenantId === value) ?? null;
    setDraft(draftFromRow(row));
    setDomainInput("");
    setFieldError("");
    setSubmitError("");
  }

  function addDomain() {
    const normalized = normalizeDomain(domainInput);
    if (!normalized) return;
    if (!isDomain(normalized)) {
      setFieldError("请填写企业域名，例如 example.com，不填写协议或路径。");
      return;
    }
    if (draft.domains.includes(normalized)) {
      setFieldError("这个域名已经在列表里。");
      return;
    }
    setDraft((value) => ({ ...value, domains: [...value.domains, normalized] }));
    setDomainInput("");
    setFieldError("");
  }

  function removeDomain(domain: string) {
    setDraft((value) => {
      const domains = value.domains.filter((item) => item !== domain);
      return {
        ...value,
        domains,
        loginPolicy: domains.length === 0 ? { ...value.loginPolicy, allowedDomainsOnly: false } : value.loginPolicy,
      };
    });
  }

  function validate(): string | null {
    if (!draft.tenantId) return "请先选择企业。";
    if (domainInput.trim()) return "请先添加域名，或清空未添加的输入内容。";
    if (draft.ssoMode !== "disabled" && draft.domains.length === 0) return "启用单点登录前至少登记一个企业域名。";
    if (draft.ssoMode === "disabled" && !draft.loginPolicy.passwordLoginEnabled) return "关闭单点登录时必须保留账号密码登录，避免企业成员无法进入。";
    if (draft.loginPolicy.allowedDomainsOnly && draft.domains.length === 0) return "限制企业域名前，请至少登记一个企业域名。";
    if (draft.reason.trim().length < 8) return "请填写至少 8 个字符的调整原因。";
    return null;
  }

  async function save() {
    const failure = validate();
    if (failure) { setSubmitError(failure); return; }
    setBusy(true);
    setSubmitError("");
    try {
      const payload: EnterpriseIdentityUpsertInput = {
        tenantId: draft.tenantId,
        domains: draft.domains.map((domain) => ({ domain })),
        sso: { mode: draft.ssoMode, providerLabel: null, metadataUrl: null },
        loginPolicy: draft.loginPolicy,
        reason: draft.reason.trim(),
        idempotencyKey: createIdempotencyKey(),
      };
      await api("/api/enterprise/identity", { method: "PUT", body: JSON.stringify(payload) });
      onDone();
    } catch (failure) {
      setSubmitError(failure instanceof Error ? failure.message : "保存失败，请稍后重试。");
    } finally {
      setBusy(false);
      setConfirmClose(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const failure = validate();
    if (failure) { setSubmitError(failure); return; }
    if (currentRow && currentRow.ssoMode !== "disabled" && draft.ssoMode === "disabled") {
      setConfirmClose(true);
      return;
    }
    void save();
  }

  function onDomainKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    addDomain();
  }

  return <>
    <Dialog
      title="配置企业身份接入"
      description="选择企业、维护域名和登录策略。保存配置不代表单点登录已验证；需要身份服务商返回证据后才算完成。"
      onClose={onClose}
      wide
    >
      <form className="form-grid identity-editor" onSubmit={submit}>
        <div className="field-block full">
          <span>企业范围</span>
          <V3Select value={draft.tenantId} onChange={chooseTenant} options={tenantOptions} ariaLabel="企业范围" placeholder="请选择企业" />
          <span className="field-hint">只显示企业名称，系统会自动关联当前选择。</span>
        </div>
        <div className="field-block full">
          <span>企业域名</span>
          <div className="domain-editor">
            <input value={domainInput} onChange={(event) => setDomainInput(event.target.value)} onKeyDown={onDomainKeyDown} placeholder="例如 example.com" />
            <button type="button" className="button secondary" onClick={addDomain}>添加域名</button>
          </div>
          <span className="field-hint">可添加多个企业域名。域名保存后仍是待验证，不能直接显示为成功。</span>
          {draft.domains.length > 0 && <div className="domain-chip-list editable" aria-label="已添加域名">
            {draft.domains.map((domain) => <span className="domain-chip unverified" key={domain}>
              <Globe2 size={13} aria-hidden />
              <span>{domain}</span>
              <small>待验证</small>
              <button type="button" onClick={() => removeDomain(domain)} aria-label={`移除 ${domain}`}>移除</button>
            </span>)}
          </div>}
        </div>
        <div className="field-block full">
          <span>登录策略</span>
          <V3Select
            value={draft.ssoMode}
            onChange={(value) => setDraft((current) => {
              const ssoMode = asSsoMode(value);
              return { ...current, ssoMode, loginPolicy: policyForMode(ssoMode, current.loginPolicy, current.domains.length) };
            })}
            options={(Object.keys(ssoModeLabels) as SsoMode[]).map((value) => ({ value, label: ssoModeLabels[value], hint: ssoModeHints[value] }))}
            ariaLabel="登录策略"
          />
          <span className="field-hint">{loginPolicyPreviewText(draft)}</span>
        </div>
        <div className="identity-policy-grid full">
          <div className="field-block">
            <span>账号密码登录</span>
            <V3Select
              value={draft.loginPolicy.passwordLoginEnabled ? "enabled" : "disabled"}
              onChange={(value) => setDraft((current) => ({ ...current, loginPolicy: { ...current.loginPolicy, passwordLoginEnabled: value === "enabled" } }))}
              options={passwordLoginOptions}
              ariaLabel="账号密码登录"
            />
          </div>
          <div className="field-block">
            <span>动态验证码</span>
            <V3Select
              value={draft.loginPolicy.mfaRequired ? "required" : "optional"}
              onChange={(value) => setDraft((current) => ({ ...current, loginPolicy: { ...current.loginPolicy, mfaRequired: value === "required" } }))}
              options={mfaOptions}
              ariaLabel="动态验证码"
            />
          </div>
          <div className="field-block">
            <span>企业域名限制</span>
            <V3Select
              value={draft.loginPolicy.allowedDomainsOnly ? "enabled" : "disabled"}
              onChange={(value) => setDraft((current) => ({ ...current, loginPolicy: { ...current.loginPolicy, allowedDomainsOnly: value === "enabled" && current.domains.length > 0 } }))}
              options={domainLimitOptions}
              ariaLabel="企业域名限制"
            />
          </div>
        </div>
        <label className="full">
          调整原因
          <textarea value={draft.reason} onChange={(event) => setDraft((current) => ({ ...current, reason: event.target.value }))} rows={3} required />
          <span className="field-hint">原因会写入审计链，用业务语言说明为什么调整。</span>
        </label>
        <div className="identity-preview full">
          <IdentityEvidenceCard
            title="保存后的单点登录状态"
            badgeValue={draft.ssoMode === "disabled" ? "not_configured" : "unverified"}
            evidence={draft.ssoMode === "disabled" ? "将关闭单点登录，不再要求身份服务商验证。" : "保存后为尚未验证，需去身份服务商完成配置与验证。"}
            time="等待平台记录"
            failure="暂无失败原因"
          />
          <IdentityEvidenceCard
            title="保存后的域名验证状态"
            badgeValue={draft.domains.length ? "unverified" : "not_configured"}
            evidence={draft.domains.length ? "域名会进入待验证状态，不会假装通过。" : "当前没有域名。"}
            time="等待平台记录"
            failure="暂无失败原因"
          />
        </div>
        {fieldError && <InlineFeedback tone="danger">{fieldError}</InlineFeedback>}
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
        <div className="form-actions full">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy}>取消</button>
          <button className="button primary" disabled={busy || !draft.tenantId}>{busy ? "正在保存…" : "保存身份接入"}</button>
        </div>
      </form>
    </Dialog>
    {confirmClose && <ConfirmDialog
      title="关闭单点登录前确认影响"
      description="这会改变企业成员的登录方式，请确认影响范围后再保存。"
      confirmLabel="确认关闭并保存"
      cancelLabel="继续编辑"
      tone="danger"
      busy={busy}
      onClose={() => setConfirmClose(false)}
      onConfirm={() => void save()}
    >
      <ul className="impact-list">
        {closeImpact(currentRow).map((item) => <li key={item}>{item}</li>)}
      </ul>
    </ConfirmDialog>}
  </>;
}

function RecoveryDialog({ row, onClose }: { row: EnterpriseIdentityConfigView; onClose: () => void }) {
  return <Dialog
    title={`${row.tenantName} 的恢复建议`}
    description="只展示业务恢复路径，不要求操作者复制任何编号。"
    onClose={onClose}
  >
    <div className="recovery-panel">
      <InlineFeedback tone={ssoStatusBadge(row) === "verified" && domainStatusBadge(row) === "verified" ? "success" : "warning"}>
        {ssoEvidenceText(row)}
      </InlineFeedback>
      <ul className="impact-list">
        {(row.recoveryActions.length ? row.recoveryActions : ["去身份服务商完成验证后，再回到本页查看核验结果。"]).map((item) => <li key={item}>{safeEvidenceText(item, "按平台恢复建议处理。")}</li>)}
      </ul>
      <footer className="dialog-footer">
        <button type="button" className="button primary" onClick={onClose}>知道了</button>
      </footer>
    </div>
  </Dialog>;
}
